import React, { useState, useEffect, useCallback } from "react";
import { GapReport } from "./types";

interface GapReportPanelProps {
  jobId: string;
  jobStatus: string;
  scraperApiUrl: string;
  getHeaders: () => Record<string, string>;
}

const HEALTH_CONFIG = {
  good: {
    bg: "bg-emerald-50",
    border: "border-emerald-200",
    badge: "bg-emerald-100 text-emerald-800 ring-1 ring-emerald-300",
    dot: "bg-emerald-500",
    label: "Excellent Coverage",
    icon: "✓",
    iconColor: "text-emerald-600"
  },
  fair: {
    bg: "bg-amber-50",
    border: "border-amber-200",
    badge: "bg-amber-100 text-amber-800 ring-1 ring-amber-300",
    dot: "bg-amber-400",
    label: "Partial Coverage",
    icon: "⚠",
    iconColor: "text-amber-600"
  },
  poor: {
    bg: "bg-red-50",
    border: "border-red-200",
    badge: "bg-red-100 text-red-800 ring-1 ring-red-300",
    dot: "bg-red-500",
    label: "Low Coverage",
    icon: "✕",
    iconColor: "text-red-600"
  }
};

const PRIORITY_CONFIG = {
  high: { badge: "bg-red-100 text-red-700", label: "High Priority" },
  medium: { badge: "bg-amber-100 text-amber-700", label: "Medium" },
  low: { badge: "bg-slate-100 text-slate-600", label: "Low" }
};

function StatCard({
  label,
  value,
  variant = "default"
}: {
  label: string;
  value: number;
  variant?: "success" | "warning" | "danger" | "muted" | "default";
}) {
  const variants = {
    success: "bg-emerald-50 border-emerald-100 text-emerald-700",
    warning: "bg-amber-50 border-amber-100 text-amber-700",
    danger: "bg-red-50 border-red-100 text-red-700",
    muted: "bg-slate-50 border-slate-100 text-slate-500",
    default: "bg-white border-slate-100 text-slate-700"
  };

  return (
    <div className={`rounded-lg border px-3 py-2 text-center flex flex-col gap-0.5 ${variants[variant]}`}>
      <span className="text-lg font-bold leading-none">{value.toLocaleString()}</span>
      <span className="text-xs font-medium">{label}</span>
    </div>
  );
}

function CoverageBar({ pct, label }: { pct: number; label: string }) {
  const color = pct >= 80 ? "bg-emerald-500" : pct >= 50 ? "bg-amber-400" : "bg-red-400";
  const textColor = pct >= 80 ? "text-emerald-700" : pct >= 50 ? "text-amber-700" : "text-red-600";

  return (
    <div>
      <div className="flex justify-between items-center mb-1.5">
        <span className="text-xs font-medium text-slate-600">{label}</span>
        <span className={`text-xs font-semibold ${textColor}`}>{pct.toFixed(0)}%</span>
      </div>
      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${color}`}
          style={{ width: `${Math.min(100, pct)}%` }}
        />
      </div>
    </div>
  );
}

export default function GapReportPanel({
  jobId,
  jobStatus,
  scraperApiUrl,
  getHeaders
}: GapReportPanelProps) {
  const [report, setReport] = useState<GapReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [showMissing, setShowMissing] = useState(false);
  const [showBlocked, setShowBlocked] = useState(false);

  const fetchReport = useCallback(async () => {
    try {
      const response = await fetch(
        `${scraperApiUrl}/jobs/${jobId}/gap-report`,
        { headers: getHeaders() }
      );
      if (response.ok) {
        const data = await response.json();
        if (data.status !== "no_report" && data.health) {
          setReport(data as GapReport);
        }
      }
    } catch {
      // Silently fail — gap report is best-effort
    } finally {
      setLoading(false);
    }
  }, [jobId, scraperApiUrl, getHeaders]);

  useEffect(() => {
    fetchReport();

    // Poll while running to pick up live gap_report from progress file
    if (jobStatus === "running" || jobStatus === "pending") {
      const interval = setInterval(fetchReport, 8000);
      return () => clearInterval(interval);
    }
  }, [jobStatus, fetchReport]);

  if (loading) {
    return (
      <tr>
        <td colSpan={6} className="p-0">
          <div className="border-t border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-400">
            Loading coverage report...
          </div>
        </td>
      </tr>
    );
  }

  if (!report) {
    return (
      <tr>
        <td colSpan={6} className="p-0">
          <div className="border-t border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-400">
            Coverage report not yet available. Complete a scrape job to see your coverage analysis.
          </div>
        </td>
      </tr>
    );
  }

  const health = HEALTH_CONFIG[report.health] ?? HEALTH_CONFIG.fair;
  const totalAttempted =
    report.pages_scraped + report.pages_blocked + report.pages_thin +
    report.pages_timeout + report.pages_filtered + report.pages_skipped;

  return (
    <tr>
      <td colSpan={6} className="p-0">
        <div className={`border-t ${health.border} ${health.bg}`}>
          {/* Header strip */}
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/60 bg-white/50">
            <div className="flex items-center gap-3">
              <span className={`w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold ${health.badge}`}>
                {health.icon}
              </span>
              <div>
                <span className="text-sm font-semibold text-slate-800">Coverage Report</span>
                <span className={`ml-2 text-xs font-medium px-2 py-0.5 rounded-full ${health.badge}`}>
                  {health.label}
                </span>
              </div>
            </div>
            <span className="text-xs text-slate-400">
              {totalAttempted.toLocaleString()} URLs attempted
            </span>
          </div>

          <div className="p-4 space-y-4">
            {/* Stat Grid */}
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
              <StatCard
                label="Indexed"
                value={report.pages_scraped}
                variant="success"
              />
              <StatCard
                label="Blocked"
                value={report.pages_blocked}
                variant={report.pages_blocked > 0 ? "danger" : "muted"}
              />
              <StatCard
                label="Thin Content"
                value={report.pages_thin}
                variant={report.pages_thin > 0 ? "warning" : "muted"}
              />
              <StatCard
                label="Timed Out"
                value={report.pages_timeout}
                variant={report.pages_timeout > 0 ? "warning" : "muted"}
              />
              <StatCard
                label="Filtered"
                value={report.pages_filtered}
                variant="muted"
              />
              <StatCard
                label="Unchanged"
                value={report.pages_skipped}
                variant="muted"
              />
            </div>

            {/* Coverage Bars */}
            <div className="bg-white rounded-lg border border-slate-100 p-3 space-y-3">
              {totalAttempted > 0 && (
                <CoverageBar
                  label="Pages Successfully Indexed"
                  pct={(report.pages_scraped / totalAttempted) * 100}
                />
              )}
              {report.sitemap_coverage_pct > 0 && (
                <CoverageBar
                  label="Sitemap Coverage"
                  pct={report.sitemap_coverage_pct}
                />
              )}
              {totalAttempted > 0 && report.pages_blocked > 0 && (
                <CoverageBar
                  label="Block Rate (lower is better)"
                  pct={report.block_rate_pct}
                />
              )}
            </div>

            {/* Recommendations */}
            {report.recommendations && report.recommendations.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
                  Recommendations
                </h4>
                <div className="space-y-1.5">
                  {report.recommendations.map((rec, i) => {
                    const pConfig = PRIORITY_CONFIG[rec.priority] ?? PRIORITY_CONFIG.low;
                    return (
                      <div
                        key={i}
                        className="flex gap-3 bg-white rounded-lg border border-slate-100 px-3 py-2.5"
                      >
                        <span
                          className={`text-xs font-medium px-1.5 py-0.5 rounded flex-shrink-0 self-start mt-0.5 ${pConfig.badge}`}
                        >
                          {pConfig.label}
                        </span>
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-slate-800 leading-snug">
                            {rec.issue}
                          </div>
                          <div className="text-xs text-slate-500 mt-0.5">{rec.suggestion}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Missing Important Pages */}
            {report.missing_important_pages && report.missing_important_pages.length > 0 && (
              <div className="space-y-1.5">
                <button
                  onClick={() => setShowMissing(!showMissing)}
                  className="flex items-center gap-1.5 text-xs font-semibold text-amber-700 hover:text-amber-900"
                >
                  <span className="w-4 h-4 rounded-full bg-amber-100 flex items-center justify-center text-amber-700 font-bold text-xs">
                    !
                  </span>
                  {report.missing_important_pages.length} Important Page
                  {report.missing_important_pages.length !== 1 ? "s" : ""} Not Found
                  <span className="text-slate-400 font-normal ml-1">
                    {showMissing ? "▲ hide" : "▼ show"}
                  </span>
                </button>
                {showMissing && (
                  <div className="flex flex-wrap gap-1.5 p-2 bg-amber-50 rounded-lg border border-amber-100">
                    {report.missing_important_pages.map((path, i) => (
                      <span
                        key={i}
                        className="text-xs px-2 py-1 rounded bg-white border border-amber-200 text-amber-700 font-mono"
                      >
                        {path}
                      </span>
                    ))}
                    <p className="w-full text-xs text-amber-600 mt-1">
                      These are common pages customers ask about. Add them to your scrape
                      URLs to improve bot answers.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Blocked URL Sample */}
            {report.missed_sitemap_pages_sample && report.missed_sitemap_pages_sample.length > 0 && (
              <div className="space-y-1.5">
                <button
                  onClick={() => setShowBlocked(!showBlocked)}
                  className="flex items-center gap-1.5 text-xs font-semibold text-red-600 hover:text-red-800"
                >
                  <span className="w-4 h-4 rounded-full bg-red-100 flex items-center justify-center text-red-600 font-bold text-xs">
                    ✕
                  </span>
                  {report.missed_sitemap_pages_sample.length} Sitemap Pages Missed
                  <span className="text-slate-400 font-normal ml-1">
                    {showBlocked ? "▲ hide" : "▼ show"}
                  </span>
                </button>
                {showBlocked && (
                  <div className="bg-red-50 rounded-lg border border-red-100 p-2 max-h-32 overflow-y-auto">
                    {report.missed_sitemap_pages_sample.map((url, i) => (
                      <div key={i} className="text-xs font-mono text-red-700 truncate py-0.5">
                        {url}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </td>
    </tr>
  );
}
